'use client'
import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { LucideLoader2 } from 'lucide-react'
import { FcGoogle } from 'react-icons/fc'
import { FaFacebook } from 'react-icons/fa6'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-background-dark text-white hover:opacity-80 dark:border dark:border-white',
        destructive:
          'bg-red-600 text-destructive-foreground hover:bg-destructive/90',
        'destructive-outline':
          'border bg-transparent border-destructive text-destructive hover:text-destructive/70 hover:border-destructive/80',
        outline:
          'border border-border bg-card/50 hover:bg-accent hover:text-accent-foreground dark:border-neutral-800',
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        accent: 'bg-accent text-accent-foreground hover:bg-accent/80',
        ghost: 'hover:bg-accent/20 bg-transparent hover:text-accent-foreground',
        link: 'text-accent underline-offset-4 hover:underline mx-0 px-0',
        'link-destructive':
          'text-destructive underline-offset-4 hover:underline mx-0 px-0',
        input: 'bg-input text-input-foreground hover:bg-input/90',

        dark: 'bg-slate-800 text-slate-100 hover:bg-slate-900',
        expandIcon:
          'group relative text-primary-foreground bg-primary hover:bg-primary/90',
        ringHover:
          'bg-primary text-primary-foreground transition-all duration-300 hover:bg-primary/90 hover:ring-2 hover:ring-primary/90 hover:ring-offset-2',
        shine:
          'text-primary-foreground animate-shine bg-gradient-to-r from-primary via-primary/75 to-primary bg-[length:400%_100%] ',
        gooeyRight:
          'text-primary-foreground relative bg-primary z-0 overflow-hidden transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5] before:rounded-[100%] before:bg-gradient-to-r from-zinc-400 before:transition-transform before:duration-1000  hover:before:translate-x-[0%] hover:before:translate-y-[0%] ',
        gooeyLeft:
          'text-primary-foreground relative bg-primary z-0 overflow-hidden transition-all duration-500 after:absolute after:inset-0 after:-z-10 after:translate-x-[-150%] after:translate-y-[150%] after:scale-[2.5] after:rounded-[100%] after:bg-gradient-to-l from-zinc-400 after:transition-transform after:duration-1000  hover:after:translate-x-[0%] hover:after:translate-y-[0%] ',
        linkHover1:
          'relative after:absolute after:bg-accent after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-left after:scale-x-100 hover:after:origin-bottom-right hover:after:scale-x-0 after:transition-transform after:ease-in-out after:duration-300',
        linkHover2:
          'relative after:absolute after:bg-primary after:bottom-2 after:h-[1px] after:w-2/3 after:origin-bottom-right after:scale-x-0 hover:after:origin-bottom-left hover:after:scale-x-100 after:transition-transform after:ease-in-out after:duration-300',
        social: 'bg-white text-black border border-gray-300 hover:bg-gray-100',
      },
      size: {
        default: 'md:h-11 h-10 px-8 text-base rounded-sm ',
        sm: 'md:h-9 h-11 rounded-sm px-6 text-sm ',
        xs: 'md:h-8 h-9 rounded-sm px-6 text-xs m-0',
        lg: 'md:h-11 h:14 rounded-sm px-8',
        icon: 'h-10 w-10 rounded-sm',
        'icon-sm': 'h-8 w-8 rounded-sm p-0 m-0', // Remove padding/margin
        'icon-xs': 'h-6 w-6 rounded-sm p-0 m-0', // Remove padding/margin
        link: 'm-0 p-0 h-fit w-fit md:h-fit md:w-fit',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
    compoundVariants: [
      { variant: 'link', size: 'xs', className: 'm-0 p-0 h-3 md:h-3' },
      { variant: 'link', size: 'sm', className: 'm-0 p-0 h-3 md:h-3' },
    ],
  }
)

interface IconProps {
  Icon: React.ElementType
  iconPlacement: 'left' | 'right'
}

interface IconRefProps {
  Icon?: never
  iconPlacement?: undefined
}

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

export type ButtonIconProps = IconProps | IconRefProps

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & ButtonIconProps
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      Icon,
      iconPlacement,
      isLoading,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    const [internalLoading, setInternalLoading] = React.useState(false)

    const handleClick = async (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      try {
        setInternalLoading(true)
        await props.onClick?.(e)
        setInternalLoading(false)
      } catch (e) {
        setInternalLoading(false)
        throw e
      }
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        onClick={handleClick}
      >
        {Icon && iconPlacement === 'left' && (
          <div className="group-hover:translate-x-100 w-0 translate-x-[0%] pr-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:pr-2 group-hover:opacity-100">
            <Icon />
          </div>
        )}
        <Slottable>{props.children}</Slottable>

        {size !== 'icon' && (
          <div
            className={cn(
              'w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 ',
              {
                'w-5 translate-x-0 pl-2 opacity-100': internalLoading,
              }
            )}
          >
            <div
              className={cn('h-fit w-fit', { 'animate-spin': internalLoading })}
            >
              <LucideLoader2 />
            </div>
          </div>
        )}
        {Icon && iconPlacement === 'right' && (
          <div className="w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
            <Icon />
          </div>
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

const GoogleButton = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={cn(
        buttonVariants({ variant: 'social', size: 'default' }),
        className
      )}
      {...props}
    >
      <FcGoogle className="mr-2 h-5 w-5" />
      Log in avec Google
    </button>
  )
}

const FacebookButton = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      className={cn(
        buttonVariants({ variant: 'social', size: 'default' }),
        className
      )}
      {...props}
    >
      <FaFacebook className="mr-2 h-5 w-5" />
      Log in avec Facebook
    </button>
  )
}

export { Button, buttonVariants, GoogleButton, FacebookButton }
